import { useEffect, useRef } from 'react';
import { findBlock, updateBlockInTree } from '../utils/blockUtils';
import {
  clearPendingDocImageFile,
  registerDocImageUploadStarter,
  rememberPendingDocImageFile,
} from '../utils/docImageUploadBridge';
import { uploadDocImage } from '../../../services/docImageService';

/**
 * Registers the upload starter for this editor instance and patches image
 * blocks as uploads progress (local → uploading → ready | error).
 * Failed uploads keep the File in the bridge so the block can Retry.
 */
export const useDocImageUpload = (docId, blocksRef, commit) => {
  const docIdRef = useRef(docId);
  docIdRef.current = docId;
  const inFlightRef = useRef(new Set());

  useEffect(() => {
    const patchProps = (blockId, propsPatch) => {
      const current = blocksRef.current;
      const block = findBlock(current, blockId);
      if (!block || block.type !== 'image') return;
      const nextProps = { ...block.props, ...propsPatch };
      // Upload status / URL swaps must not create undo steps — otherwise
      // Ctrl+Z peels "ready → uploading → local" instead of removing the insert.
      commit(updateBlockInTree(current, blockId, { props: nextProps }), {
        record: false,
      });
    };

    const startUpload = async (blockId, file) => {
      if (!blockId || !file) return;
      if (inFlightRef.current.has(blockId)) return;
      inFlightRef.current.add(blockId);
      rememberPendingDocImageFile(blockId, file);

      const id = docIdRef.current;
      if (!id) {
        patchProps(blockId, {
          uploadStatus: 'error',
          errorMessage: 'Save the doc before adding images',
        });
        inFlightRef.current.delete(blockId);
        return;
      }

      patchProps(blockId, {
        uploadStatus: 'uploading',
        errorMessage: undefined,
      });

      try {
        const result = await uploadDocImage(id, file);
        const block = findBlock(blocksRef.current, blockId);
        const localUrl = block?.props?.localPreviewUrl;
        if (localUrl?.startsWith('blob:')) {
          try {
            URL.revokeObjectURL(localUrl);
          } catch {
            /* ignore */
          }
        }
        clearPendingDocImageFile(blockId);
        patchProps(blockId, {
          publicUrl: result.publicUrl,
          imageId: result.imageId,
          r2Key: result.r2Key,
          fileName: result.fileName,
          mimeType: result.mimeType,
          size: result.size,
          uploadStatus: 'ready',
          localPreviewUrl: undefined,
          errorMessage: undefined,
        });
      } catch (err) {
        // Keep pending File so Retry can re-send without re-picking.
        // Surface a short fixed label — raw API text ("Backend not available…")
        // is noisy for this UI.
        console.warn('Doc image upload failed:', err?.message || err);
        patchProps(blockId, {
          uploadStatus: 'error',
          errorMessage: 'Upload failed',
        });
      } finally {
        inFlightRef.current.delete(blockId);
      }
    };

    registerDocImageUploadStarter(startUpload);
    return () => registerDocImageUploadStarter(null);
  }, [blocksRef, commit]);
};
